import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const API = '/api';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    plus: ['M12 5v14', 'M5 12h14'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    map: ['M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z', 'M8 2v16', 'M16 6v16'],
    upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M7 10l5 5 5-5 M12 15V3',
    truck: ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z', 'M18.5 21a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z'],
    user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z'],
    check: ['M20 6L9 17l-5-5'],
    calendar: ['M8 2v4', 'M16 2v4', 'M3 10h18', 'M21 8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8z'],
};

/* ── Availability Panel ── */
function AvailabilityPanel({ onClose, vehicles, drivers, vendorFleet, trips }) {
    const [tab, setTab] = useState('fleet'); // fleet | drivers | vfleet | vdrivers
    const [search, setSearch] = useState('');
    const HOURS = Array.from({ length: 24 }, (_, i) => i);

    const isOnTrip = (entityId, hour) => {
        const activeTrips = trips.filter(t => {
            const vid = t.vehicleId?._id || t.vehicleId;
            const did = t.driverId?._id || t.driverId;
            const vfid = t.vendorProposedVehicleId?._id || t.vendorProposedVehicleId;
            return (vid === entityId || did === entityId || vfid === entityId) &&
                ['Scheduled', 'Ongoing'].includes(t.status);
        });
        return activeTrips.some(t => {
            if (!t.startTime) return false;
            const [sh] = t.startTime.split(':').map(Number);
            const [eh] = (t.endTime || t.startTime).split(':').map(Number);
            return hour >= sh && hour <= (eh || sh);
        });
    };

    const TABS = [
        { key: 'fleet', label: 'General Fleet', count: vehicles.length },
        { key: 'drivers', label: 'General Drivers', count: drivers.length },
        { key: 'vfleet', label: 'Vendor Fleet', count: vendorFleet.length },
        { key: 'vdrivers', label: 'Vendor Drivers', count: 0 },
    ];

    const rows = (() => {
        const q = search.toLowerCase();
        if (tab === 'fleet') return vehicles.filter(v => (v.registrationNumber + (v.make || '')).toLowerCase().includes(q)).map(v => ({ id: v._id, name: v.registrationNumber, sub: v.make + ' ' + (v.model || ''), avatar: '🚗' }));
        if (tab === 'drivers') return drivers.filter(d => d.name?.toLowerCase().includes(q)).map(d => ({ id: d._id, name: d.name, sub: d.phoneNumber || '', avatar: (d.name || 'D')[0] }));
        if (tab === 'vfleet') return vendorFleet.filter(f => (f.registrationNumber || '').toLowerCase().includes(q)).map(f => ({ id: f._id, name: f.registrationNumber, sub: f.vehicleType || f.make || '', avatar: '🚌' }));
        return [];
    })();

    const availCount = rows.filter(r => !HOURS.some(h => isOnTrip(r.id, h))).length;
    const onTripCount = rows.length - availCount;

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
            <div style={{ background: '#fff', borderRadius: 20, width: '92%', maxWidth: 900, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{ padding: '18px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e1b4b' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Icon d={icons.calendar} size={18} strokeWidth={2} />
                        <span style={{ color: 'white', fontWeight: 800, fontSize: 16 }}>Availability Overview</span>
                        <span style={{ background: 'rgba(255,255,255,0.15)', color: 'white', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>Today</span>
                    </div>
                    <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon d={icons.x} size={16} />
                    </button>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                    {TABS.map(t => (
                        <button key={t.key} onClick={() => { setTab(t.key); setSearch(''); }}
                            style={{ flex: 1, padding: '12px 8px', border: 'none', cursor: 'pointer', background: tab === t.key ? '#fff' : 'transparent', borderBottom: tab === t.key ? '2px solid #4f46e5' : '2px solid transparent', fontWeight: tab === t.key ? 800 : 600, color: tab === t.key ? '#4f46e5' : '#64748b', fontSize: 13, transition: 'all 0.15s' }}>
                            {t.label}
                            <span style={{ marginLeft: 6, background: tab === t.key ? '#e0e7ff' : '#f1f5f9', color: tab === t.key ? '#4f46e5' : '#94a3b8', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{t.count}</span>
                        </button>
                    ))}
                </div>

                {/* Summary Cards */}
                <div style={{ display: 'flex', gap: 12, padding: '14px 20px 0', background: '#fff' }}>
                    {[
                        { label: 'Total', val: rows.length, bg: '#eff6ff', color: '#1d4ed8' },
                        { label: 'Available', val: availCount, bg: '#f0fdf4', color: '#166534' },
                        { label: 'On Trips', val: onTripCount, bg: '#fef3c7', color: '#92400e' },
                    ].map(s => (
                        <div key={s.label} style={{ flex: 1, background: s.bg, borderRadius: 12, padding: '10px 16px', textAlign: 'center' }}>
                            <div style={{ fontSize: 26, fontWeight: 900, color: s.color }}>{s.val}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: s.color, opacity: 0.75 }}>{s.label}</div>
                        </div>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '0 8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: '#374151' }}><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: '#4ade80' }} /> Available</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: '#374151' }}><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: '#f87171' }} /> On Trip</div>
                    </div>
                </div>

                {/* Search */}
                <div style={{ padding: '10px 20px', background: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', padding: '0 12px' }}>
                        <Icon d={icons.search} size={13} />
                        <input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${TABS.find(t => t.key === tab)?.label}...`}
                            style={{ flex: 1, border: 'none', outline: 'none', padding: '9px 0', fontSize: 13, background: 'transparent', fontFamily: 'inherit' }} />
                    </div>
                </div>

                {/* Grid */}
                <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto', padding: '0 20px 20px' }}>
                    {tab === 'vdrivers' ? (
                        <div style={{ textAlign: 'center', padding: '50px 20px', color: '#94a3b8' }}>
                            <Icon d={icons.user} size={36} />
                            <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600 }}>Vendor driver availability coming soon</div>
                        </div>
                    ) : rows.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '50px 20px', color: '#94a3b8', fontSize: 13 }}>No records found</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#94a3b8', whiteSpace: 'nowrap', minWidth: 160 }}>Name</th>
                                    <th style={{ textAlign: 'left', padding: '8px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', whiteSpace: 'nowrap', width: 80 }}>Hours</th>
                                    {HOURS.map(h => (
                                        <th key={h} style={{ padding: '4px 2px', fontSize: 9, fontWeight: 700, color: '#94a3b8', textAlign: 'center', width: 26 }}>
                                            {h === 0 ? '12' : h > 12 ? h - 12 : h}
                                            <div style={{ fontSize: 8, color: '#cbd5e1' }}>{h < 12 ? 'AM' : 'PM'}</div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => {
                                    const busyHours = HOURS.filter(h => isOnTrip(r.id, h));
                                    return (
                                        <tr key={r.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                                            <td style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                                                <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e0e7ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{r.avatar}</div>
                                                <div>
                                                    <div style={{ fontWeight: 700, fontSize: 12, color: '#1e293b' }}>{r.name}</div>
                                                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.sub}</div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '8px', fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{busyHours.length > 0 ? `${busyHours.length * 60} mins` : '0 mins'}</td>
                                            {HOURS.map(h => (
                                                <td key={h} style={{ padding: '3px 2px' }}>
                                                    <div title={isOnTrip(r.id, h) ? 'On Trip' : 'Available'} style={{ width: 22, height: 22, borderRadius: 4, background: isOnTrip(r.id, h) ? '#f87171' : '#4ade80', border: '1.5px solid rgba(255,255,255,0.6)', cursor: 'default' }} />
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function Trips({ user, onNavigate }) {
    const [trips, setTrips] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({ date: '', client: '', route: '', billingType: '' });
    const [modal, setModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [vendorFleet, setVendorFleet] = useState([]);
    const [importing, setImporting] = useState(false);
    const [approvalModal, setApprovalModal] = useState(null);
    const fileInputRef = useRef(null);

    const operatorId = user?.operatorId || user?._id || 'all';

    useEffect(() => {
        fetchData();
        fetchCommonData();
    }, []);

    const fetchCommonData = async () => {
        try {
            const [clientRes, routeRes, vehicleRes, driverRes, vendorRes, vfRes] = await Promise.all([
                axios.get(`${API}/clients/${operatorId}`),
                axios.get(`${API}/routes/${operatorId}`),
                axios.get(`${API}/vehicles/${operatorId}`),
                axios.get(`${API}/drivers/${operatorId}`),
                axios.get(`${API}/vendors/${operatorId}`),
                axios.get(`${API}/vendor-fleet/${operatorId}`)
            ]);
            setClients(clientRes.data);
            setRoutes(routeRes.data);
            setVehicles(vehicleRes.data);
            setDrivers(driverRes.data);
            setVendors(vendorRes.data);
            setVendorFleet(vfRes.data);
        } catch (err) { console.error(err); }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/trips/${operatorId}`);
            setTrips(res.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Client': 'Infosys Ltd',
                'Route': 'IT Park Shuttle',
                'Trip Type': 'Login',
                'Frequency': 'Recurring',
                'Recurring Days': 'Mon,Tue,Wed,Thu,Fri',
                'Trip Date': '',
                'Start Time': '08:00',
                'End Time': '09:00',
                'Vehicle Plate': 'DXB-1234',
                'Driver Name': 'John Doe',
                'Amount': 500,
                'Billing Category': 'Daily',
                'Status': 'Scheduled'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Trips');
        XLSX.writeFile(wb, 'Trips_Template.xlsx');
    };

    const handleImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImporting(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const data = XLSX.utils.sheet_to_json(ws);

                const mapped = data.map(row => ({
                    client: row['Client'],
                    route: row['Route'],
                    tripType: row['Trip Type'] || 'Login',
                    tripFrequency: row['Frequency'] || 'One Time',
                    recurringDays: row['Recurring Days'] ? row['Recurring Days'].split(',').map(d => d.trim()) : [],
                    tripDate: row['Trip Date'],
                    startTime: row['Start Time'],
                    endTime: row['End Time'],
                    vehiclePlate: row['Vehicle Plate'],
                    driverName: row['Driver Name'],
                    amount: row['Amount'],
                    billingType: row['Billing Category'] || 'Daily',
                    status: row['Status'] || 'Scheduled'
                }));

                const res = await axios.post(`${API}/trips/import`, { operatorId, trips: mapped });
                if (res.status === 201) {
                    alert(`${res.data.successCount} trips imported!`);
                    fetchData();
                }
            } catch (err) {
                alert('Import failed. Please check your data format.');
            } finally {
                setImporting(false);
                e.target.value = '';
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleApprove = async (trip, extra) => {
        try {
            let rid = trip.routeId?._id || trip.routeId;
            if (!rid) {
                const routeRes = await axios.post(`${API}/routes`, {
                    name: extra.routeName,
                    operatorId,
                    clientId: trip.clientId?._id || trip.clientId,
                    pickupLocation: trip.pickupLocation,
                    dropLocation: trip.dropLocation,
                    distance: extra.distance || 0,
                    status: 'Active'
                });
                rid = routeRes.data._id;
            }
            await axios.put(`${API}/trips/${trip._id}`, {
                ...trip,
                routeId: rid,
                amount: extra.amount,
                status: 'Scheduled'
            });
            alert('Trip request approved and scheduled successfully!');
            setApprovalModal(null);
            fetchData();
        } catch (err) {
            console.error('Error approving trip:', err);
            alert('Failed to approve trip request.');
        }
    };

    const acceptVendorProposal = async (trip) => {
        try {
            await axios.put(`${API}/trips/${trip._id}`, {
                vendorStatus: 'Accepted',
                vehicleId: trip.vendorProposedVehicleId?._id || trip.vendorProposedVehicleId,
                driverId: trip.vendorProposedDriverId?._id || trip.vendorProposedDriverId,
                amount: trip.vendorProposedAmount,
                status: 'Scheduled'
            });
            fetchData();
        } catch (err) { console.error(err); alert('Failed to accept proposal'); }
    };

    const rejectVendorProposal = async (trip) => {
        try {
            await axios.put(`${API}/trips/${trip._id}`, { vendorStatus: 'Rejected' });
            fetchData();
        } catch (err) { console.error(err); alert('Failed to reject proposal'); }
    };

    const handleSave = async (formData) => {
        try {
            // Clean payload
            const payload = { ...formData, operatorId };

            // Ensure all ID fields are either a valid ID or null/removed (Mongoose fails on empty strings)
            const sanitizeId = (id) => {
                if (!id) return null;
                if (typeof id === 'object') return id._id || null;
                return id === '' ? null : id;
            };

            payload.clientId = sanitizeId(payload.clientId);
            payload.routeId = sanitizeId(payload.routeId);
            payload.vehicleId = sanitizeId(payload.vehicleId);
            payload.driverId = sanitizeId(payload.driverId);
            payload.vendorId = sanitizeId(payload.vendorId);

            // Clean up tripDate (Mongoose rejects empty strings for Date fields)
            if (!payload.tripDate || payload.tripFrequency === 'Recurring') {
                payload.tripDate = null;
            }

            // Remove Mongoose-specific meta fields if they exist
            delete payload.__v;
            delete payload.createdAt;
            delete payload.updatedAt;

            if (modal.data?._id) {
                await axios.put(`${API}/trips/${modal.data._id}`, payload);
                alert('Trip schedule updated successfully!');
            } else {
                // Remove _id if it somehow leaked into new form state
                delete payload._id;
                await axios.post(`${API}/trips`, payload);
                alert('Trip schedule created successfully!');
            }
            setModal(null);
            fetchData();
        } catch (err) {
            console.error('Error saving trip:', err);
            alert(`Failed to save trip: ${err.response?.data?.message || err.message}`);
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${API}/trips/${deleteModal._id}`);
            setDeleteModal(null);
            fetchData();
        } catch (err) {
            console.error(err);
            alert('Error deleting trip');
        }
    };

    const filtered = trips.filter(t => {
        const matchesSearch = (
            t.routeId?.name?.toLowerCase().includes(search.toLowerCase()) ||
            t.clientId?.companyName?.toLowerCase().includes(search.toLowerCase()) ||
            t.vendorId?.vendorName?.toLowerCase().includes(search.toLowerCase()) ||
            t.passengerName?.toLowerCase().includes(search.toLowerCase()) ||
            t.passengerPhone?.includes(search)
        );
        const matchesClient = !filters.client || t.clientId?._id === filters.client || t.clientId === filters.client;
        const matchesRoute = !filters.route || t.routeId?._id === filters.route || t.routeId === filters.route;
        const matchesBilling = !filters.billingType || t.billingType === filters.billingType;
        const matchesDate = !filters.date || (t.tripDate && t.tripDate.startsWith(filters.date));

        return matchesSearch && matchesClient && matchesRoute && matchesDate && matchesBilling;
    });

    return (
        <div className="md-page">
            <div className="md-header">
                <div>
                    <h1 className="md-title">Trips Management</h1>
                    <p className="md-subtitle">Schedule and optimize vehicle trips for clients.</p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <input type="file" ref={fileInputRef} hidden accept=".xlsx,.xls" onChange={handleImport} />
                    <button className="premium-btn btn-import" onClick={() => fileInputRef.current.click()} disabled={importing}>
                        <Icon d={icons.upload} size={18} strokeWidth={2.5} />
                        {importing ? 'Importing...' : 'Import'}
                    </button>
                    <button className="premium-btn btn-template" onClick={downloadTemplate}>
                        <Icon d={icons.download} size={18} strokeWidth={2.5} />
                        Template
                    </button>
                    <button className="premium-btn btn-primary-action" onClick={() => setModal({ data: null })}>
                        <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                        Add Trip
                    </button>
                </div>
            </div>

            {/* Pending Requests Notification Bar */}
            {trips.filter(t => t.status === 'Requested').length > 0 && (
                <div style={{
                    marginBottom: 24, padding: '16px 24px', borderRadius: 16,
                    background: 'linear-gradient(90deg, #1e1b4b 0%, #312e81 100%)',
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    boxShadow: '0 10px 20px -5px rgba(30, 27, 75, 0.3)', animation: 'slideIn 0.5s ease'
                }}>
                    <style>{`@keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                        <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon d={icons.map} size={20} />
                        </div>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 800 }}>{trips.filter(t => t.status === 'Requested').length} Pending Requests</div>
                            <div style={{ fontSize: 13, opacity: 0.8, fontWeight: 500 }}>Clients have submitted new trip requirements awaiting your approval.</div>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            const firstReq = trips.find(t => t.status === 'Requested');
                            setApprovalModal(firstReq);
                        }}
                        style={{ padding: '10px 20px', borderRadius: 12, border: 'none', background: 'white', color: '#1e1b4b', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}
                    >
                        Review Latest Request
                    </button>
                </div>
            )}

            {/* Vendor Proposal Alert Banner */}
            {trips.filter(t => t.vendorStatus === 'Vendor Proposed').length > 0 && (
                <div style={{
                    marginBottom: 24, padding: '16px 24px', borderRadius: 16,
                    background: 'linear-gradient(90deg, #065f46 0%, #059669 100%)',
                    color: 'white', display: 'flex', alignItems: 'center', gap: 15,
                    boxShadow: '0 10px 20px -5px rgba(6,95,70,0.35)'
                }}>
                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon d={icons.truck} size={22} />
                    </div>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 800 }}>
                            {trips.filter(t => t.vendorStatus === 'Vendor Proposed').length} Vendor Proposal{trips.filter(t => t.vendorStatus === 'Vendor Proposed').length > 1 ? 's' : ''} Awaiting Your Approval
                        </div>
                        <div style={{ fontSize: 13, opacity: 0.85, fontWeight: 500 }}>
                            Vendor(s) have submitted vehicle, driver and pricing — review and accept in the Vehicle & Driver column below.
                        </div>
                    </div>
                </div>
            )}

            <div className="md-panel">

                <div className="md-panel-toolbar" style={{ flexWrap: 'wrap', gap: '15px' }}>
                    <div className="md-search-wrap" style={{ minWidth: '250px' }}>
                        <Icon d={icons.search} size={14} />
                        <input
                            className="md-input md-search"
                            placeholder="Search trips..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flex: 1 }}>
                        <select className="md-input" value={filters.client} onChange={e => setFilters({ ...filters, client: e.target.value })} style={{ height: '40px' }}>
                            <option value="">All Clients</option>
                            {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                        </select>
                        <select className="md-input" value={filters.billingType} onChange={e => setFilters({ ...filters, billingType: e.target.value })} style={{ height: '40px' }}>
                            <option value="">All Billing</option>
                            <option value="Ad-hoc">Adhoc</option>
                            <option value="Daily">Daily</option>
                            <option value="Shuttle">Shuttle</option>
                            <option value="Shuttle with Booking">Shuttle with Booking</option>
                        </select>
                        <select className="md-input" value={filters.route} onChange={e => setFilters({ ...filters, route: e.target.value })} style={{ height: '40px' }}>
                            <option value="">All Routes</option>
                            {routes.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                        </select>
                        <input type="date" className="md-input" value={filters.date} onChange={e => setFilters({ ...filters, date: e.target.value })} style={{ height: '40px' }} />
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Loading trips...</div>
                ) : (
                    <div className="md-table-wrap">
                        <table className="md-table">
                            <thead>
                                <tr>
                                    <th className="md-th">Client</th>
                                    <th className="md-th">Route Details</th>
                                    <th className="md-th">Vehicle & Driver</th>
                                    <th className="md-th">Schedule</th>
                                    <th className="md-th">Trip Fee</th>
                                    <th className="md-th">Status</th>
                                    <th className="md-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(t => (
                                    <tr key={t._id}>
                                        <td className="md-td">
                                            {t.tripType === 'on_demand' ? (
                                                <div>
                                                    <div style={{ fontWeight: 700, color: '#4f46e5' }}>{t.passengerName || 'Adhoc Guest'}</div>
                                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{t.passengerPhone}</div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <div style={{ fontWeight: 700, color: '#1e1b4b' }}>{t.clientId?.companyName || '—'}</div>
                                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>ID: {t._id.slice(-6).toUpperCase()}</div>
                                                </div>
                                            )}
                                        </td>
                                        <td className="md-td">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{
                                                    padding: '4px 8px', borderRadius: 6,
                                                    background: t.billingType?.includes('Shuttle') ? '#f0fdf4' : (t.billingType === 'Ad-hoc' ? '#fef2f2' : '#f1f5f9'),
                                                    color: t.billingType?.includes('Shuttle') ? '#166534' : (t.billingType === 'Ad-hoc' ? '#991b1b' : '#64748b'),
                                                    fontSize: 10, fontWeight: 700
                                                }}>
                                                    {t.billingType || 'Daily'}
                                                </div>
                                                <div style={{
                                                    padding: '4px 8px', borderRadius: 6,
                                                    background: t.tripType === 'on_demand' ? '#e0e7ff' : '#f1f5f9',
                                                    color: t.tripType === 'on_demand' ? '#4338ca' : '#64748b',
                                                    fontSize: 10, fontWeight: 700
                                                }}>
                                                    {t.tripType === 'on_demand' ? 'Adhoc' : (t.tripType || 'Fixed')}
                                                </div>
                                                <strong>{t.tripType === 'on_demand' ? 'Adhoc Trip' : (t.routeId?.name || '—')}</strong>
                                            </div>
                                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                                                {t.tripType === 'on_demand' ? `${t.pickupLocation} ➔ ${t.dropLocation}` : t.tripFrequency}
                                            </div>
                                        </td>
                                        <td className="md-td">
                                            {t.vendorId && t.vendorStatus === 'Vendor Proposed' ? (
                                                <div style={{ background: '#eff6ff', borderRadius: 10, padding: '10px 12px', border: '1.5px solid #bfdbfe' }}>
                                                    <div style={{ fontSize: 10, fontWeight: 800, color: '#1d4ed8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 4 }}><Icon d={icons.truck} size={10} /> Vendor Proposal</div>
                                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af' }}>{t.vendorId?.vendorName}</div>
                                                    <div style={{ fontSize: 11, color: '#374151', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}><Icon d={icons.truck} size={11} /> {t.vendorProposedVehicleId?.registrationNumber || '—'}</div>
                                                    <div style={{ fontSize: 11, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}><Icon d={icons.user} size={11} /> {t.vendorProposedDriverId?.name || '—'}</div>
                                                    <div style={{ fontSize: 12, fontWeight: 800, color: '#059669', marginTop: 3 }}>₹{t.vendorProposedAmount || '—'}</div>
                                                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                                        <button
                                                            onClick={() => acceptVendorProposal(t)}
                                                            style={{ flex: 1, padding: '5px 0', background: '#10b981', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                                                        ><Icon d={icons.check} size={11} color="white" /> Accept</button>
                                                        <button
                                                            onClick={() => rejectVendorProposal(t)}
                                                            style={{ flex: 1, padding: '5px 0', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                                                        ><Icon d={icons.x} size={11} /> Reject</button>
                                                    </div>
                                                </div>
                                            ) : t.vendorId ? (
                                                <div>
                                                    <div style={{ fontWeight: 600, color: '#4f46e5' }}>{t.vendorId.vendorName}</div>
                                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                                        {t.vendorStatus === 'Accepted' ? `✓ ${t.vehicleId?.registrationNumber || t.vendorProposedVehicleId?.registrationNumber || 'No Vehicle'}` :
                                                            t.vendorStatus === 'Rejected' ? '✗ Proposal Rejected' :
                                                                'Awaiting vendor response...'}
                                                    </div>
                                                </div>
                                            ) : t.vehicleId ? (
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>{t.vehicleId.plateNumber || t.vehicleId.registrationNumber || '—'}</div>
                                                    <div style={{ fontSize: 11, color: '#64748b' }}>{t.driverId?.name || 'No Driver'}</div>
                                                </div>
                                            ) : (
                                                <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Unassigned</span>
                                            )}
                                        </td>
                                        <td className="md-td">
                                            <div style={{ fontSize: 12 }}>
                                                {t.tripFrequency === 'One Time' ? (
                                                    t.tripDate ? new Date(t.tripDate).toLocaleDateString() : '—'
                                                ) : (
                                                    <div style={{ display: 'flex', gap: 4 }}>
                                                        {t.recurringDays?.map(d => (
                                                            <span key={d} style={{ background: '#e0e7ff', color: '#4338ca', padding: '2px 4px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{d.charAt(0)}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {t.startTime && (
                                                <div style={{ fontSize: 11, color: '#1e1b4b', fontWeight: 700, marginTop: 4 }}>
                                                    {t.startTime} {t.endTime ? `- ${t.endTime}` : ''}
                                                </div>
                                            )}
                                        </td>
                                        <td className="md-td">
                                            <div style={{ fontWeight: 600, color: '#10b981' }}>{t.amount ? `₹${t.amount}` : '—'}</div>
                                        </td>
                                        <td className="md-td">
                                            <span className={`md-badge md-badge-${t.status?.toLowerCase()}`}>{t.status}</span>
                                        </td>
                                        <td className="md-td">
                                            <div className="md-actions">
                                                {t.status === 'Requested' && (
                                                    <button
                                                        onClick={() => setApprovalModal(t)}
                                                        style={{
                                                            padding: '6px 12px', borderRadius: 8, border: 'none',
                                                            background: '#10b981', color: 'white', fontSize: 11,
                                                            fontWeight: 800, cursor: 'pointer', marginRight: 8
                                                        }}
                                                    >
                                                        Approve
                                                    </button>
                                                )}
                                                {t.tripType !== 'on_demand' && (
                                                    <button className="md-icon-btn" onClick={() => setModal({ data: t })}><Icon d={icons.edit} size={14} /></button>
                                                )}
                                                <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal(t)}><Icon d={icons.trash} size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Approval Modal */}
            {approvalModal && (
                <div className="md-modal-overlay" onClick={() => setApprovalModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header">
                            <h3 className="md-modal-title">Approve Trip Request</h3>
                            <button className="md-modal-close" onClick={() => setApprovalModal(null)}><Icon d={icons.x} size={18} /></button>
                        </div>
                        <div className="md-modal-body">
                            <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, marginBottom: 20, border: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Client Request</div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: '#1e1b4b', marginTop: 4 }}>{approvalModal.clientId?.companyName}</div>
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                                    <span style={{ fontWeight: 700 }}>Pickup:</span> {approvalModal.pickupLocation}
                                </div>
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                                    <span style={{ fontWeight: 700 }}>Drop:</span> {approvalModal.dropLocation}
                                </div>
                            </div>

                            {!approvalModal.routeId && (
                                <div className="md-form-group" style={{ marginBottom: 20 }}>
                                    <label className="md-label">Create New Route As*</label>
                                    <input
                                        className="md-input"
                                        placeholder="Enter name for this new route (e.g. IT Park Shuttle)"
                                        onChange={(e) => approvalModal._routeName = e.target.value}
                                    />
                                    <p style={{ fontSize: 11, color: '#6366f1', marginTop: 6, fontWeight: 600 }}>* This request proposed a new route. We'll create it automatically.</p>
                                </div>
                            )}

                            <div className="md-form-group">
                                <label className="md-label">Trip Fee / Amount (₹)*</label>
                                <input
                                    type="number"
                                    className="md-input"
                                    placeholder="Enter trip amount for client billing"
                                    onChange={(e) => approvalModal._amount = e.target.value}
                                    required
                                />
                            </div>
                        </div>
                        <div className="md-modal-footer">
                            <button className="md-cancel-btn" onClick={() => setApprovalModal(null)}>Ignore</button>
                            <button
                                className="md-save-btn"
                                onClick={() => handleApprove(approvalModal, {
                                    routeName: approvalModal._routeName,
                                    amount: approvalModal._amount
                                })}
                            >
                                Confirm & Schedule Trip
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal */}
            {modal && (
                <TripForm
                    key={modal.data?._id || 'new'}
                    data={modal.data}
                    clients={clients}
                    routes={routes}
                    vehicles={vehicles}
                    drivers={drivers}
                    vendors={vendors}
                    vendorFleet={vendorFleet}
                    trips={trips}
                    onClose={() => setModal(null)} onNavigate={onNavigate}
                    onSave={handleSave}
                />
            )}

            {/* Delete Confirmation */}
            {deleteModal && (
                <div className="md-modal-overlay" onClick={() => setDeleteModal(null)}>
                    <div className="md-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <div className="md-modal-header">
                            <h3 className="md-modal-title" style={{ color: '#ef4444' }}>Confirm Delete</h3>
                        </div>
                        <div className="md-modal-body">
                            Are you sure you want to delete this trip schedule?
                        </div>
                        <div className="md-modal-footer">
                            <button className="md-cancel-btn" onClick={() => setDeleteModal(null)}>Cancel</button>
                            <button className="md-save-btn" style={{ background: '#ef4444' }} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function TripForm({ onNavigate, data, clients, routes, vehicles, drivers, vendors, vendorFleet, trips, onClose, onSave }) {
    const [form, setForm] = useState(data ? {
        ...data,
        clientId: data.clientId?._id || data.clientId || '',
        routeId: data.routeId?._id || data.routeId || '',
        vehicleId: data.vehicleId?._id || data.vehicleId || '',
        driverId: data.driverId?._id || data.driverId || '',
        vendorId: data.vendorId?._id || data.vendorId || '',
        amount: data.amount || '',
        billingType: data.billingType || 'Daily'
    } : {
        clientId: '', routeId: '',
        amount: '',
        billingType: 'Daily',
        pickupLocation: '', dropLocation: '',
        tripType: 'Login', tripFrequency: 'One Time',
        tripDate: '', recurringDays: [], status: 'Scheduled',
        startTime: '', endTime: '',
        vehicleId: '', driverId: '', vendorId: ''
    });

    const [routeStops, setRouteStops] = useState([]);
    const [isVendorTrip, setIsVendorTrip] = useState(!!form.vendorId);
    const [availPanel, setAvailPanel] = useState(null); // 'fleet' | 'drivers' | 'vfleet'

    const mapRef = useRef(null);
    const pickupInputRef = useRef(null);
    const dropInputRef = useRef(null);
    const googleMap = useRef(null);
    const directionsRenderer = useRef(null);
    const directionsService = useRef(null);

    useEffect(() => {
        if (!window.google) return;

        googleMap.current = new window.google.maps.Map(mapRef.current, {
            center: { lat: 20.5937, lng: 78.9629 },
            zoom: 5,
            disableDefaultUI: true,
            zoomControl: true,
            styles: [
                { "featureType": "administrative", "elementType": "geometry", "stylers": [{ "visibility": "off" }] },
                { "featureType": "poi", "stylers": [{ "visibility": "off" }] },
                { "featureType": "road", "elementType": "labels.icon", "stylers": [{ "visibility": "off" }] },
                { "featureType": "transit", "stylers": [{ "visibility": "off" }] }
            ]
        });

        directionsService.current = new window.google.maps.DirectionsService();
        directionsRenderer.current = new window.google.maps.DirectionsRenderer({
            map: googleMap.current,
            polylineOptions: { strokeColor: '#6366f1', strokeWeight: 5, strokeOpacity: 0.8 },
            markerOptions: { opacity: 0.8 }
        });

        const pickupAutocomplete = new window.google.maps.places.Autocomplete(pickupInputRef.current);
        const dropAutocomplete = new window.google.maps.places.Autocomplete(dropInputRef.current);

        pickupAutocomplete.addListener('place_changed', () => {
            const place = pickupAutocomplete.getPlace();
            if (place.formatted_address) setForm(prev => ({ ...prev, pickupLocation: place.formatted_address }));
        });

        dropAutocomplete.addListener('place_changed', () => {
            const place = dropAutocomplete.getPlace();
            if (place.formatted_address) setForm(prev => ({ ...prev, dropLocation: place.formatted_address }));
        });

        if (form.routeId) fetchRouteStops(form.routeId);
        if (form.pickupLocation && form.dropLocation) calculateRoute(form.pickupLocation, form.dropLocation);
    }, []);

    const fetchRouteStops = async (routeId) => {
        try {
            const res = await axios.get(`${API}/route-stops/${routeId}`);
            setRouteStops(res.data);
            showStopsOnMap(res.data);
        } catch (err) { console.error(err); }
    };

    const showStopsOnMap = (stops) => {
        if (!window.google || !googleMap.current) return;
        if (stops.length > 0) {
            googleMap.current.setCenter({ lat: stops[0].lat, lng: stops[0].lng });
            googleMap.current.setZoom(13);
        }
        stops.forEach(stop => {
            new window.google.maps.Marker({
                position: { lat: stop.lat, lng: stop.lng },
                map: googleMap.current,
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 6,
                    fillColor: "#fff",
                    fillOpacity: 1,
                    strokeColor: "#6366f1",
                    strokeWeight: 2,
                },
                title: stop.stopName || stop.name
            });
        });
    };

    useEffect(() => {
        if (form.pickupLocation && form.dropLocation) calculateRoute(form.pickupLocation, form.dropLocation);
    }, [form.pickupLocation, form.dropLocation]);

    const calculateRoute = (origin, destination) => {
        if (!window.google || !directionsService.current) return;
        directionsService.current.route({
            origin, destination, travelMode: window.google.maps.TravelMode.DRIVING
        }, (res, status) => {
            if (status === 'OK') directionsRenderer.current.setDirections(res);
        });
    };

    const toggleDay = (day) => {
        const days = form.recurringDays.includes(day)
            ? form.recurringDays.filter(d => d !== day)
            : [...form.recurringDays, day];
        setForm({ ...form, recurringDays: days });
    };

    return (
        <>
            {availPanel && (
                <AvailabilityPanel
                    onClose={() => setAvailPanel(null)}
                    vehicles={vehicles}
                    drivers={drivers}
                    vendorFleet={vendorFleet}
                    trips={trips || []}
                />
            )}
            <div className="md-modal-overlay" onClick={onClose}>
                <div className="md-modal" style={{ maxWidth: 1100, width: '95%' }} onClick={e => e.stopPropagation()}>
                    <div className="md-modal-header">
                        <h3 className="md-modal-title">{data ? 'Edit Trip Schedule' : 'Create New Trip'}</h3>
                        <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                    </div>
                    <div className="md-modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1.25fr', gap: '30px', padding: 25 }}>
                        <div className="form-fields" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div className="md-form-group">
                                <label className="md-label">Client*</label>
                                <select className="md-input" value={form.clientId?._id || form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })}>
                                    <option value="">Select Client</option>
                                    {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                                </select>
                            </div>
                            <div className="md-form-group">
                                <label className="md-label">Trip Type*</label>
                                <select className="md-input" value={form.tripType} onChange={e => setForm({ ...form, tripType: e.target.value })}>
                                    <option value="Login">Login (Pickup)</option>
                                    <option value="Logout">Logout (Drop)</option>
                                </select>
                            </div>
                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Billing Category*</label>
                                <select className="md-input" value={form.billingType} onChange={e => setForm({ ...form, billingType: e.target.value })}>
                                    <option value="Shuttle">Shuttle (Monthly Billing)</option>
                                    <option value="Shuttle with Booking">Shuttle with Booking</option>
                                    <option value="Daily">Daily Billing (Per Trip)</option>
                                    <option value="Ad-hoc">Ad-hoc Billing</option>
                                </select>
                            </div>
                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Route Selection*</label>
                                <select
                                    className="md-input"
                                    value={form.routeId}
                                    onChange={e => {
                                        const rid = e.target.value;
                                        const r = routes.find(x => x._id === rid);
                                        setForm({
                                            ...form,
                                            routeId: rid,
                                            pickupLocation: r?.pickupLocation || '',
                                            dropLocation: r?.dropLocation || ''
                                        });
                                        if (rid) fetchRouteStops(rid);
                                    }}
                                >
                                    <option value="">Link to an existing route...</option>
                                    {routes.map(r => <option key={r._id} value={r._id}>{r.name} ({r.distance}km)</option>)}
                                </select>
                            </div>

                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <div style={{ display: 'flex', gap: 15, alignItems: 'center', marginBottom: 8, padding: '10px 12px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                                    <label className="md-label" style={{ margin: 0, fontSize: 13, userSelect: 'none', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            style={{ marginRight: 8 }}
                                            checked={isVendorTrip}
                                            onChange={e => {
                                                setIsVendorTrip(e.target.checked);
                                                if (!e.target.checked) setForm(prev => ({ ...prev, vendorId: '', vehicleId: '', driverId: '' }));
                                            }}
                                        />
                                        Assign to Vendor?
                                    </label>
                                </div>

                                {isVendorTrip ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: 10 }}>
                                        <div className="md-form-group">
                                            <label className="md-label">Vendor</label>
                                            <select className="md-input" value={form.vendorId} onChange={e => setForm({ ...form, vendorId: e.target.value, vehicleId: '', driverId: '' })}>
                                                <option value="">Select Vendor</option>
                                                {vendors.map(v => <option key={v._id} value={v._id}>{v.vendorName}</option>)}
                                            </select>
                                        </div>
                                        <div className="md-form-group">
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                                <label className="md-label" style={{ margin: 0 }}>Vendor Vehicle</label>
                                                <button type="button" onClick={() => { onClose(); onNavigate && onNavigate('availability'); }} title="Check vendor fleet availability" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 7, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                                    <Icon d={icons.calendar} size={11} strokeWidth={2} /> Availability
                                                </button>
                                            </div>
                                            <select className="md-input" value={form.vehicleId} onChange={e => setForm({ ...form, vehicleId: e.target.value })}>
                                                <option value="">Select Vehicle</option>
                                                {vendorFleet.filter(f => f.vendorId?._id === form.vendorId || f.vendorId === form.vendorId).map(f => (
                                                    <option key={f._id} value={f._id}>{f.registrationNumber} ({f.vehicleType})</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: 10 }}>
                                        <div className="md-form-group">
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                                <label className="md-label" style={{ margin: 0 }}>Vehicle</label>
                                                <button type="button" onClick={() => { onClose(); onNavigate && onNavigate('availability'); }} title="Check fleet availability" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 7, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                                    <Icon d={icons.calendar} size={11} strokeWidth={2} /> Availability
                                                </button>
                                            </div>
                                            <select className="md-input" value={form.vehicleId} onChange={e => setForm({ ...form, vehicleId: e.target.value })}>
                                                <option value="">Select Vehicle</option>
                                                {vehicles.map(v => <option key={v._id} value={v._id}>{v.registrationNumber} - {v.make}</option>)}
                                            </select>
                                        </div>
                                        <div className="md-form-group">
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                                <label className="md-label" style={{ margin: 0 }}>Driver</label>
                                                <button type="button" onClick={() => { onClose(); onNavigate && onNavigate('availability'); }} title="Check driver availability" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 7, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                                                    <Icon d={icons.calendar} size={11} strokeWidth={2} /> Availability
                                                </button>
                                            </div>
                                            <select className="md-input" value={form.driverId} onChange={e => setForm({ ...form, driverId: e.target.value })}>
                                                <option value="">Select Driver</option>
                                                {drivers.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Pickup (Autocomplete)*</label>
                                <input ref={pickupInputRef} className="md-input" value={form.pickupLocation} onChange={e => setForm({ ...form, pickupLocation: e.target.value })} placeholder="Start location" />
                            </div>
                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Drop (Autocomplete)*</label>
                                <input ref={dropInputRef} className="md-input" value={form.dropLocation} onChange={e => setForm({ ...form, dropLocation: e.target.value })} placeholder="End location" />
                            </div>
                            <div className="md-form-group">
                                <label className="md-label">Frequency*</label>
                                <select className="md-input" value={form.tripFrequency} onChange={e => setForm({ ...form, tripFrequency: e.target.value })}>
                                    <option value="One Time">One Time</option>
                                    <option value="Recurring">Recurring</option>
                                </select>
                            </div>
                            {form.tripFrequency === 'One Time' ? (
                                <div className="md-form-group">
                                    <label className="md-label">Trip Date*</label>
                                    <input type="date" className="md-input" value={form.tripDate ? String(form.tripDate).split('T')[0] : ''} onChange={e => setForm({ ...form, tripDate: e.target.value })} />
                                </div>
                            ) : (
                                <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                    <label className="md-label">Recurring Days</label>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                            <button
                                                key={day}
                                                type="button"
                                                onClick={() => toggleDay(day)}
                                                style={{
                                                    flex: 1, minWidth: 40, padding: '8px 0', borderRadius: 8, border: '1px solid #e2e8f0',
                                                    background: form.recurringDays.includes(day) ? '#1e1b4b' : '#fff',
                                                    color: form.recurringDays.includes(day) ? '#fff' : '#64748b',
                                                    fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s'
                                                }}
                                            >
                                                {day.charAt(0)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="md-form-group">
                                <label className="md-label">Start Time*</label>
                                <input type="time" className="md-input" value={form.startTime || ''} onChange={e => setForm({ ...form, startTime: e.target.value })} />
                            </div>
                            <div className="md-form-group">
                                <label className="md-label">End Time</label>
                                <input type="time" className="md-input" value={form.endTime || ''} onChange={e => setForm({ ...form, endTime: e.target.value })} />
                            </div>
                            <div className="md-form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="md-label">Trip Amount / Fee</label>
                                <input type="number" className="md-input" placeholder="0.00" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                            <div style={{ flex: 1, position: 'relative', borderRadius: 20, overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                                <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
                                {!form.routeId && !form.pickupLocation && (
                                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(248,30,252,0.02)', backdropFilter: 'blur(2px)', color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 40 }}>
                                        <div>
                                            <Icon d={icons.map} size={32} />
                                            <p style={{ marginTop: 10 }}>Select a route or locations to preview on map</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {routeStops.length > 0 && (
                                <div style={{ padding: '15px', background: '#f8fafc', borderRadius: 16, border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e1b4b', marginBottom: 10 }}>Route Stops ({routeStops.length})</div>
                                    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 5 }}>
                                        {routeStops.map((s, idx) => (
                                            <div key={s._id} style={{ background: '#fff', padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0', whiteSpace: 'nowrap', fontSize: 11, color: '#64748b' }}>
                                                <span style={{ fontWeight: 700, marginRight: 5 }}>{idx + 1}.</span> {s.stopName}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="md-modal-footer" style={{ borderTop: '1px solid #f1f5f9', padding: '15px 25px' }}>
                        <button className="md-cancel-btn" onClick={onClose}>Discard</button>
                        <button className="md-save-btn" onClick={() => onSave(form)}>Confirm Trip Schedule</button>
                    </div>
                </div>
            </div>
        </>
    );
}

